import { authRoutes, authErrorURL } from '@shared/constants/authRoutes'
import { AccessLevel } from '@shared/constants/accessLevel'
import {
  type PCIRouteName,
  pciRouteDictionary,
  pciRouteMapByURL,
} from '@shared/constants/routes.pci'
import { isOnServer } from '@shared/constants/util'
import { toKebabCase } from '@shared/utils/url'

export type ShiptRoute = {
  url: string
  trackingName: string
  pageTitle?: string
  /** Enable for pages that require a store context */
  storeRequired?: true
  noIndex?: true
  /** True for routes that manually decide whether or not to redirect the user. */
  selfGuardsAccess: boolean
  /** The access requirement for a route */
  access: AccessLevel
}

export const RouteName = {
  '360': '360',
  ABOUT: 'ABOUT',
  ACCESSIBILITY: 'ACCESSIBILITY',
  ADDRESS: 'ADDRESS',
  ADVANCED_SECURITY: 'ADVANCED_SECURITY',
  AMBASSADOR: 'AMBASSADOR',
  ASSISTANT_LANDING: 'ASSISTANT_LANDING',
  AUTH0_ERROR: 'AUTH0_ERROR',
  BLOG: 'BLOG',
  BLOG_POST: 'BLOG_POST',
  BRANCH: 'BRANCH',
  BRANCH_UNGATED: 'BRANCH_UNGATED',
  BUSINESS_ADD_PROFILE: 'BUSINESS_ADD_PROFILE',
  BUSINESS_DETAILS: 'BUSINESS_DETAILS',
  BUSINESS_MANAGE_USERS: 'BUSINESS_MANAGE_USERS',
  BUSINESS_TOOLS: 'BUSINESS_TOOLS',
  BUY_AGAIN: 'BUY_AGAIN',
  CANCELLATION: 'CANCELLATION',
  CART: 'CART',
  CART_BUILDER: 'CART_BUILDER',
  CART_CONVERSION_LOADING: 'CART_CONVERSION_LOADING',
  CHECKOUT: 'CHECKOUT',
  CHOOSE_MEMBERSHIP: 'CHOOSE_MEMBERSHIP',
  CITIES: 'CITIES',
  CMS_BULK_CONTENT_PREVIEW: 'CMS_BULK_CONTENT_PREVIEW',
  CMS_CATCHALL: 'CMS_CATCHALL',
  CMS_MODULE: 'CMS_MODULE',
  CMS_PREVIEW_PAGE: 'CMS_PREVIEW_PAGE',
  COMMUNITY: 'COMMUNITY',
  CONTACT: 'CONTACT',
  CORPORATE: 'CORPORATE',
  COUPON: 'COUPON',
  COUPONS: 'COUPONS',
  DATA_PRIVACY: 'DATA_PRIVACY',
  DEALS: 'DEALS',
  DELIVERY: 'DELIVERY',
  DIETARY_PREFERENCES: 'DIETARY_PREFERENCES',
  DISCOVER: 'DISCOVER',
  DRIVER: 'DRIVER',
  DRIVER_INTAKE_FORM: 'DRIVER_INTAKE_FORM',
  EMAIL_VERIFICATION_REQUIRED: 'EMAIL_VERIFICATION_REQUIRED',
  FEATURED_PROMOTIONS: 'FEATURED_PROMOTIONS',
  GARDENGRANTS: 'GARDENGRANTS',
  GIFT: 'GIFT',
  GLOBAL_HOMEPAGE: 'GLOBAL_HOMEPAGE',
  GLOBAL_LANDING_PAGE: 'GLOBAL_LANDING_PAGE',
  GLOBAL_SEARCH: 'GLOBAL_SEARCH',
  GRANTS: 'GRANTS',
  GUEST_WELCOME: 'GUEST_WELCOME',
  HI: 'HI',
  INVITES: 'INVITES',
  INVITES_INTERIM: 'INVITES_INTERIM',
  KITS: 'KITS',
  KIT_DETAIL: 'KIT_DETAIL',
  LADDERUP: 'LADDERUP',
  LANDING_PAGE: 'LANDING_PAGE',
  LANDING_PAGE_FOR_YOU: 'LANDING_PAGE_FOR_YOU',
  LEGAL: 'LEGAL',
  LIST: 'LIST',
  LISTS_HUB: 'LISTS_HUB',
  LIST_SHARE: 'LIST_SHARE',
  LIST_SHARED_DETAIL: 'LIST_SHARED_DETAIL',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  LP: 'LP',
  MEMBERSHIP_DASHBOARD: 'MEMBERSHIP_DASHBOARD',
  NEED_ANYTHING_ELSE: 'NEED_ANYTHING_ELSE',
  NOTIFICATIONS: 'NOTIFICATIONS',
  OFFER: 'OFFER',
  ONE_PASS: 'ONE_PASS',
  ORDER_CONFIRMATION: 'ORDER_CONFIRMATION',
  ORDER_DETAIL: 'ORDER_DETAIL',
  ORDER_LIST: 'ORDER_LIST',
  PARTNER: 'PARTNER',
  PASSWORD_RESET: 'PASSWORD_RESET',
  PERPLEXITY: 'PERPLEXITY',
  PREFERRED: 'PREFERRED',
  PRICING: 'PRICING',
  PRIVACY_RIGHTS_REQUESTS: 'PRIVACY_RIGHTS_REQUESTS',
  PRODUCT: 'PRODUCT',
  PROFILE: 'PROFILE',
  REDEEM: 'REDEEM',
  REDEEM_LOGIN: 'REDEEM_LOGIN',
  REDEEM_PAYMENT: 'REDEEM_PAYMENT',
  REDEEM_SIGNUP: 'REDEEM_SIGNUP',
  RETAILER_COUPON: 'RETAILER_COUPON',
  RETAILER_COUPONS: 'RETAILER_COUPONS',
  RETAILER_DEALS: 'RETAILER_DEALS',
  RETAILER_FEATURED_PROMOTIONS: 'RETAILER_FEATURED_PROMOTIONS',
  RETAILER_HOMEPAGE: 'RETAILER_HOMEPAGE',
  RETAILER_KITS: 'RETAILER_KITS',
  RETAILER_KIT_DETAIL: 'RETAILER_KIT_DETAIL',
  RETAILER_LANDING_PAGE: 'RETAILER_LANDING_PAGE',
  RETAILER_SHOPPABLE_VIDEOS: 'RETAILER_SHOPPABLE_VIDEOS',
  RETAILER_SHOPPABLE_VIDEO_DETAIL: 'RETAILER_SHOPPABLE_VIDEO_DETAIL',
  REWARD: 'REWARD',
  SEARCH: 'SEARCH',
  SEARCH_REDIRECT: 'SEARCH_REDIRECT',
  SECURE_ADD_GIFT_CARD_PAYMENT: 'SECURE_ADD_GIFT_CARD_PAYMENT',
  SECURE_ADD_MEMBERSHIP_PAYMENT: 'SECURE_ADD_MEMBERSHIP_PAYMENT',
  SECURE_ADD_PAYMENT: 'SECURE_ADD_PAYMENT',
  SECURE_CHOOSE_MEMBERSHIP_PAYMENT: 'SECURE_CHOOSE_MEMBERSHIP_PAYMENT',
  SHOPPABLE_VIDEOS: 'SHOPPABLE_VIDEOS',
  SHOPPABLE_VIDEO_DETAIL: 'SHOPPABLE_VIDEO_DETAIL',
  SHOPPER: 'SHOPPER',
  SHOPPER_ALREADY_APPLIED: 'SHOPPER_ALREADY_APPLIED',
  SIGN_UP: 'SIGN_UP',
  SITEMAP_INDEX: 'SITEMAP_INDEX',
  SPECIAL_REQUESTS: 'SPECIAL_REQUESTS',
  STORES: 'STORES',
  STUDENT: 'STUDENT',
  SUBSTITUTION_PREFERENCES: 'SUBSTITUTION_PREFERENCES',
  SUBSTITUTION_PREFERENCES_ORDER: 'SUBSTITUTION_PREFERENCES_ORDER',
  TARGET_CIRCLE_360: 'TARGET_CIRCLE_360',
  UNGATED_CATEGORY: 'UNGATED_CATEGORY',
  UNGATED_CATEGORY_HUB: 'UNGATED_CATEGORY_HUB',
  UNGATED_HOMEPAGE: 'UNGATED_HOMEPAGE',
  UNGATED_PROMOTIONS_PAGE: 'UNGATED_PROMOTIONS_PAGE',
  UNIVERSAL_PRODUCT: 'UNIVERSAL_PRODUCT',
  WALLET: 'WALLET',
  WELCOME: 'WELCOME',
} as const
export type RouteName = (typeof RouteName)[keyof typeof RouteName]

type NonCmsRouteDictionary = {
  [P in Exclude<RouteName, CmsRouteName | PCIRouteName>]: ShiptRoute
}
const nonCmsRouteDictionary = {
  // @ts-expect-error -- about.tsx is not managed in CMS, /about/[...slug] is, so we need to make an exception here
  ABOUT: {
    access: AccessLevel.Public,
    pageTitle: 'About Shipt - Same Day Delivery',
    selfGuardsAccess: false,
    trackingName: 'about',
    url: '/about',
  },
  ACCESSIBILITY: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Accessibility',
    selfGuardsAccess: false,
    trackingName: 'accessibility',
    url: '/shop/account/accessibility',
  },
  ADDRESS: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Addresses',
    selfGuardsAccess: false,
    trackingName: 'addresses',
    url: '/shop/account/addresses',
  },
  ADVANCED_SECURITY: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'advanced_security',
    url: '/shop/account/advanced-security',
  },
  ASSISTANT_LANDING: {
    access: AccessLevel.Public,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'assistant_landing',
    url: '/assistant',
  },
  AUTH0_ERROR: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'auth_error',
    url: authErrorURL,
  },
  BLOG: {
    access: AccessLevel.Public,
    pageTitle: 'Blog - Recipes & More from Shipt | Same-Day Delivery',
    selfGuardsAccess: false,
    trackingName: 'blog',
    url: '/blog',
  },
  BLOG_POST: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'blog_post',
    url: '/blog',
  },
  BRANCH: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'branch_landing',
    url: '/shop/branch',
  },
  BRANCH_UNGATED: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'branch_landing',
    url: '/shop/branch/u',
  },
  BUSINESS_ADD_PROFILE: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Add Shipt Business profile',
    selfGuardsAccess: false,
    trackingName: 'add_business_profile',
    url: '/shop/add-business-profile',
  },
  BUSINESS_DETAILS: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Business details',
    selfGuardsAccess: false,
    trackingName: 'business_details',
    url: '/shop/account/business-details',
  },
  BUSINESS_MANAGE_USERS: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Manage users',
    selfGuardsAccess: false,
    trackingName: 'business_manage_users',
    url: '/shop/account/business-manage-users',
  },
  BUSINESS_TOOLS: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Business tools',
    selfGuardsAccess: false,
    trackingName: 'business_tools',
    url: '/shop/account/business-tools',
  },
  BUY_AGAIN: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Buy Again',
    selfGuardsAccess: false,
    trackingName: 'buy_again',
    url: '/shop/buy_again',
  },
  CANCELLATION: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'cancellation',
    url: '/shop/membership-dashboard/cancellation',
  },
  CART: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: false,
    storeRequired: true,
    trackingName: 'cart',
    url: '/shop/cart',
  },
  CART_BUILDER: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Cart Builder',
    selfGuardsAccess: false,
    storeRequired: true,
    trackingName: 'cart_builder',
    url: '/shop/cart-builder',
  },
  CART_CONVERSION_LOADING: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'cart_conversion_loading',
    url: '/shop/cart/loading',
  },
  CHOOSE_MEMBERSHIP: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    pageTitle: 'Choose Membership',
    selfGuardsAccess: false,
    trackingName: 'choose_membership',
    url: '/shop/choose-membership',
  },
  CMS_BULK_CONTENT_PREVIEW: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'cms',
    url: '/cms/bulk-content-preview',
  },
  CMS_MODULE: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'cms',
    url: '/cms/module',
  },
  CMS_PREVIEW_PAGE: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'cms',
    url: '/cms/page',
  },
  CONTACT: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'contact',
    url: '/contact',
  },
  COUPON: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: true,
    trackingName: 'coupon',
    url: '/shop/coupon',
  },
  COUPONS: {
    access: AccessLevel.UserOrGuest,
    noIndex: true, // no index, as this is a fallback route
    selfGuardsAccess: true,
    trackingName: 'coupons',
    url: '/shop/coupons',
  },
  DATA_PRIVACY: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Data Privacy',
    selfGuardsAccess: false,
    trackingName: 'data_privacy',
    url: '/shop/account/data-privacy',
  },
  DEALS: {
    access: AccessLevel.UserOrGuest,
    noIndex: true, // no index, as this is a fallback route
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'savings',
    url: '/shop/deals',
  },
  DIETARY_PREFERENCES: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'dietary_preferences',
    url: '/shop/account/dietary-preferences',
  },
  DRIVER_INTAKE_FORM: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'driver_intake_form',
    url: '/privacy/driver-intake-form',
  },
  EMAIL_VERIFICATION_REQUIRED: {
    access: AccessLevel.Public,
    pageTitle: 'Email Verification Required',
    selfGuardsAccess: false,
    trackingName: 'email_verification_required',
    url: '/email-verification-required',
  },
  FEATURED_PROMOTIONS: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    pageTitle: 'Featured Promotion',
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'cpg_promotions',
    url: '/shop/featured-promotions',
  },
  GLOBAL_HOMEPAGE: {
    access: AccessLevel.UserOrGuest,
    selfGuardsAccess: true,
    trackingName: 'global_home',
    url: '/shop',
  },
  GLOBAL_LANDING_PAGE: {
    access: AccessLevel.UserOrGuest,
    selfGuardsAccess: true,
    trackingName: 'global-landing-page',
    url: '/shop/stores/landing-page',
  },
  GLOBAL_SEARCH: {
    access: AccessLevel.UserOrGuest,
    selfGuardsAccess: true,
    trackingName: 'global_search',
    url: '/shop/stores/search',
  },
  GUEST_WELCOME: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'guest_welcome',
    url: '/shop/guest-welcome',
  },
  INVITES: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Group Invites',
    selfGuardsAccess: false,
    trackingName: 'invites',
    url: '/shop/account/invites',
  },
  // This route is used to get around an AASA issue at launch; will be removed after 8/26
  // https://shipt.clickup.com/t/36127442/86bbfrppx
  INVITES_INTERIM: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Group Invites',
    selfGuardsAccess: false,
    trackingName: 'invites',
    url: '/group-invites',
  },
  KITS: {
    access: AccessLevel.Public,
    pageTitle: 'Kits',
    selfGuardsAccess: false,
    trackingName: 'global_shipt_kits',
    url: '/shop/kits',
  },
  KIT_DETAIL: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'global_shipt_kits_detail',
    url: '/shop/kits',
  },
  LANDING_PAGE: {
    access: AccessLevel.UserOrGuest,
    noIndex: true, // no index, as this is a fallback route
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'landing-page',
    url: '/shop/landing-page',
  },
  LANDING_PAGE_FOR_YOU: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: true,
    trackingName: 'landing-page-for-you',
    url: '/shop/landing-page/for-you',
  },
  LIST: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: false,
    storeRequired: true,
    trackingName: 'shopping_list',
    url: '/shop/lists',
  },
  LISTS_HUB: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: false,
    storeRequired: true,
    trackingName: 'shopping_lists_hub',
    url: '/shop/lists',
  },
  LIST_SHARE: {
    access: AccessLevel.Public,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'shopping_list_share',
    url: '/shop/lists/share',
  },
  LIST_SHARED_DETAIL: {
    access: AccessLevel.Public,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'shopping_list_shared_detail',
    url: '/shop/lists/shared',
  },
  LOGIN: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'login',
    url: '/login',
  },
  LOGOUT: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'logout',
    url: '/logout',
  },
  MEMBERSHIP_DASHBOARD: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Membership Dashboard',
    selfGuardsAccess: false,
    trackingName: 'member_dashboard',
    url: '/shop/membership-dashboard',
  },
  NEED_ANYTHING_ELSE: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    storeRequired: true,
    trackingName: 'PostCartUpsell',
    url: '/shop/need-anything-else',
  },
  NOTIFICATIONS: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Notifications',
    selfGuardsAccess: false,
    trackingName: 'notifications',
    url: '/shop/account/notifications',
  },
  ONE_PASS: {
    access: AccessLevel.Public,
    pageTitle: 'Discover the Shipt Difference - Learn More - Shipt Same Day Delivery',
    selfGuardsAccess: false,
    trackingName: 'onepass',
    url: '/onepass',
  },
  ORDER_CONFIRMATION: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'order-confirmation',
    url: '/shop/order-confirmation',
  },
  ORDER_DETAIL: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Orders',
    selfGuardsAccess: false,
    trackingName: 'order_detail',
    url: '/shop/account/orders',
  },
  ORDER_LIST: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Orders',
    selfGuardsAccess: false,
    trackingName: 'order_list',
    url: '/shop/account/orders',
  },
  PASSWORD_RESET: {
    access: AccessLevel.Public,
    pageTitle: 'Password Reset',
    selfGuardsAccess: false,
    trackingName: 'password_reset',
    url: '/password-reset',
  },
  PREFERRED: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'preferred',
    url: '/shop/account/preferred',
  },
  PRIVACY_RIGHTS_REQUESTS: {
    access: AccessLevel.Public,
    pageTitle: 'Privacy Rights Requests',
    selfGuardsAccess: false,
    trackingName: 'privacy_rights_requests',
    url: '/privacy/intake-form',
  },
  PRODUCT: {
    // PDP admits visitors by bootstrapping a guest session from the zip header,
    // so access can only be decided AFTER data fetching. Middleware skips it.
    access: AccessLevel.UserOrGuest,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'detail',
    url: '/shop/products',
  },
  PROFILE: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Profile',
    selfGuardsAccess: false,
    trackingName: 'profile',
    url: '/shop/account/profile',
  },
  REDEEM_LOGIN: {
    access: AccessLevel.Public,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'redeem_login',
    url: '/shop/redeem/login',
  },
  REDEEM_PAYMENT: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'redeem',
    url: '/shop/redeem/payment',
  },
  REDEEM_SIGNUP: {
    access: AccessLevel.Public,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'redeem',
    url: '/shop/redeem',
  },
  RETAILER_COUPON: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'coupon',
    url: '/shop/[store_slug]/coupon',
  },
  RETAILER_COUPONS: {
    access: AccessLevel.UserOrGuest,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'coupons',
    url: '/shop/[store_slug]/coupons',
  },
  RETAILER_DEALS: {
    access: AccessLevel.UserOrGuest,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'savings',
    url: '/shop/[store_slug]/deals',
  },
  RETAILER_FEATURED_PROMOTIONS: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    pageTitle: 'Featured Promotion',
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'cpg_promotions',
    url: '/shop/[store_slug]/featured-promotions',
  },
  RETAILER_HOMEPAGE: {
    access: AccessLevel.UserOrGuest,
    pageTitle: 'Home',
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'home',
    url: '/shop/[store_slug]',
  },
  RETAILER_KITS: {
    access: AccessLevel.Public,
    pageTitle: 'Kits',
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'retailer_shipt_kits',
    url: '/shop/[store_slug]/kits',
  },
  RETAILER_KIT_DETAIL: {
    access: AccessLevel.Public,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'retailer_shipt_kits_detail',
    url: '/shop/[store_slug]/kits',
  },
  RETAILER_LANDING_PAGE: {
    access: AccessLevel.UserOrGuest,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'landing-page',
    url: '/shop/[store_slug]/landing-page',
  },
  RETAILER_SHOPPABLE_VIDEOS: {
    access: AccessLevel.Public,
    pageTitle: 'Watch & Shop',
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'retailer_shoppable_videos',
    url: '/shop/[store_slug]/shoppable-videos',
  },
  RETAILER_SHOPPABLE_VIDEO_DETAIL: {
    access: AccessLevel.Public,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'retailer_shoppable_video_detail',
    url: '/shop/[store_slug]/shoppable-videos',
  },
  REWARD: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Rewards',
    selfGuardsAccess: false,
    trackingName: 'rewards',
    url: '/shop/account/rewards',
  },
  SEARCH: {
    access: AccessLevel.UserOrGuest,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'search',
    url: '/shop/[store_slug]/search',
  },
  SEARCH_REDIRECT: {
    access: AccessLevel.UserOrGuest,
    noIndex: true,
    selfGuardsAccess: true,
    storeRequired: true,
    trackingName: 'search_redirect',
    url: '/shop/search',
  },
  SHOPPABLE_VIDEOS: {
    access: AccessLevel.Public,
    pageTitle: 'Watch & Shop',
    selfGuardsAccess: false,
    trackingName: 'global_shoppable_videos',
    url: '/shop/shoppable-videos',
  },
  SHOPPABLE_VIDEO_DETAIL: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'global_shoppable_video_detail',
    url: '/shop/shoppable-videos',
  },
  SHOPPER_ALREADY_APPLIED: {
    access: AccessLevel.Public,
    pageTitle: 'Duplicate Shopper Application',
    selfGuardsAccess: false,
    trackingName: 'shopper-already-applied',
    url: '/shopper-already-applied',
  },
  SIGN_UP: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'sign_up',
    url: '/signup',
  },
  SITEMAP_INDEX: {
    access: AccessLevel.Public,
    pageTitle: 'Sitemap',
    selfGuardsAccess: false,
    trackingName: 'sitemap_index',
    url: '/sitemap-index',
  },
  SPECIAL_REQUESTS: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'special_requests',
    url: '/shop/special-requests',
  },
  SUBSTITUTION_PREFERENCES: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    storeRequired: true,
    trackingName: 'substitution_preferences',
    url: '/shop/substitution-preferences',
  },
  SUBSTITUTION_PREFERENCES_ORDER: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    selfGuardsAccess: false,
    trackingName: 'substitution_preferences_order',
    url: '/shop/account/orders/substitution-preferences',
  },
  TARGET_CIRCLE_360: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'target_circle_360',
    url: '/target-circle-360',
  },
  UNGATED_CATEGORY: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'ungated_category_page',
    url: '/categories',
  },
  UNGATED_CATEGORY_HUB: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'ungated_category_hub',
    url: '/categories',
  },
  UNGATED_HOMEPAGE: {
    access: AccessLevel.Public,
    pageTitle: 'Your Local Stores Delivered - Shipt Same-Day Delivery',
    selfGuardsAccess: false,
    trackingName: 'homepage_redesign',
    url: '/',
  },
  UNGATED_PROMOTIONS_PAGE: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'ungated_promotions_page',
    url: '/promotions',
  },
  UNIVERSAL_PRODUCT: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'ungated_product_details',
    url: '/up',
  },
  WALLET: {
    access: AccessLevel.UserOnly,
    noIndex: true,
    pageTitle: 'Wallet',
    selfGuardsAccess: false,
    trackingName: 'wallet',
    url: '/shop/account/wallet',
  },
  WELCOME: {
    access: AccessLevel.Public,
    selfGuardsAccess: false,
    trackingName: 'welcome',
    url: '/welcome',
  },
} as const satisfies NonCmsRouteDictionary

const cmsRouteNames = [
  RouteName['360'],
  RouteName.ABOUT,
  RouteName.AMBASSADOR,
  RouteName.CITIES,
  RouteName.COMMUNITY,
  RouteName.CORPORATE,
  RouteName.DELIVERY,
  RouteName.DISCOVER,
  RouteName.DRIVER,
  RouteName.GARDENGRANTS,
  RouteName.GIFT,
  RouteName.GRANTS,
  RouteName.HI,
  RouteName.LADDERUP,
  RouteName.LEGAL,
  RouteName.LP,
  RouteName.OFFER,
  RouteName.PARTNER,
  RouteName.PERPLEXITY,
  RouteName.PRICING,
  RouteName.REDEEM,
  RouteName.SHOPPER,
  RouteName.STORES,
  RouteName.STUDENT,
  RouteName.CMS_CATCHALL,
] as const

type CmsRouteName = (typeof cmsRouteNames)[number]

type CmsRouteDictionary = {
  [P in CmsRouteName]: ShiptRoute
}
const cmsRouteDictionary = cmsRouteNames.reduce<CmsRouteDictionary>(
  (acc, routeName) => ({
    ...acc,
    [routeName]: {
      access: AccessLevel.Public,
      selfGuardsAccess: false,
      // CMS routes are handled by the catch all route, so no need to define a tracking name.
      trackingName: '',
      url: `/${routeName.toLowerCase()}`,
    } satisfies ShiptRoute,
  }),
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions -- cast as CmsRouteDictionary
  {} as CmsRouteDictionary,
)

type RouteDictionary = {
  [P in (typeof RouteName)[keyof typeof RouteName]]: ShiptRoute
}
export const routes = {
  ...cmsRouteDictionary,
  ...nonCmsRouteDictionary,
  ...pciRouteDictionary,
} as const satisfies RouteDictionary

// These are dynamic CMS route urls that we want to be able to reference within Segway
export const cmsRoutes = {
  DELIVERY: {
    ALCOHOL_DELIVERY: '/delivery/alcohol-delivery',
    ALCOHOL_DELIVERY_NEAR_YOU: '/delivery/alcohol-delivery/cities',
    ELECTRONICS_DELIVERY: '/delivery/electronics-delivery',
    GROCERY_DELIVERY: '/delivery/grocery-delivery',
    HOUSEHOLD_ESSENTIALS_DELIVERY: '/delivery/household-essentials-delivery',
    PET_SUPPLIES_DELIVERY: '/delivery/pet-supplies-delivery',
    SEASONAL_DELIVERY: '/delivery/seasonal',
  },
  DRIVER: {
    BE_A_DRIVER: '/driver/be-a-driver',
  },
  SHIPT_BUSINESS: '/business',
  SHOPPER: {
    BE_A_SHOPPER: '/shopper/be-a-shopper',
    FAQ: '/shopper/shopper-faq',
    LIFE: '/shopper/shopper-life',
    PERKS: '/shopper/shopper-perks',
    SAFETY: '/shopper/shopper-safety',
  },
}

export function getRouteData<T extends RouteName>(routeName: T) {
  const routeData = routes[routeName]
  return { ...routeData, routeName }
}

export enum AccountPageReferrers {
  GLOBAL_HOME_PAGE = 'home',
}

export const isValidRoute = (pathname: string) => {
  return (
    Object.keys(basePaths).some((route) => pathname.startsWith(route)) ||
    Object.values(authRoutes).some((route) => pathname === route)
  )
}

export const SKIP_SSR = 'skipSsr'

/**
 * Note to future engineers/agents: if a new slugified route is added to pages/shop/[store_slug] directory, the following needs to happen in this file and tests:
 * - a getRetailer* helper needs to be added e.g. getRetailerDealsUrl
 * - the new route must be added to retailerSlugRoutes below
 * - tests in routes.spec.ts must be updated to assert the new route is handled correctly by getRetailerBaseUrl route
 */
const retailerSlugRoutes = [
  '/shop/search',
  '/shop/landing-page',
  '/shop/deals',
  '/shop/coupons',
  '/shop/coupon',
  '/shop/featured-promotions',
  '/shop/kits',
]
export const getRetailerBaseUrl = (
  storeName: string | undefined,
  url: string | undefined,
): string | undefined => {
  if (!url || !storeName) {
    return url
  }

  // url base needs to match retailer slug routes
  let matchingFirstPart = false
  for (const route of retailerSlugRoutes) {
    if (url.startsWith(route)) {
      const nextChar = url[route.length]
      if (nextChar === undefined || nextChar === '/' || nextChar === '?' || nextChar === '#') {
        matchingFirstPart = true
        break
      }
    }
  }

  if (!matchingFirstPart && url !== '/shop') return url

  const newStart = `/shop/${toKebabCase(storeName)}`
  return url.replace('/shop', newStart)
}
export const getRetailerUrl = (
  storeName: string | undefined,
  options?: {
    // skips data fetching on SSR for the retailer homepage on client side navigations
    skipSsr?: true
  },
) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerUrl: storeName is missing')
  }

  // We need to reference routes.RETAILER_HOMEPAGE.url directly for this util
  // oxlint-disable-next-line eslint-js/no-restricted-syntax
  const url = routes.RETAILER_HOMEPAGE.url.replace('[store_slug]', toKebabCase(storeName))
  return options?.skipSsr ? `${url}?${SKIP_SSR}=true` : url
}

const buildGHPFallbackUrlWithReason = (reason: string) => {
  return `${routes.GLOBAL_HOMEPAGE.url}?redirect_from_invalid_url=${encodeURIComponent(reason)}`
}
export const getRetailerSearchUrl = (storeName: string | undefined) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerSearchUrl: storeName is missing')
  }

  return `${getRetailerUrl(storeName)}/search`
}

export const getRetailerDealsUrl = (storeName: string | undefined) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerDealsUrl: storeName is missing')
  }

  return `${getRetailerUrl(storeName)}/deals`
}

export const getRetailerCouponsUrl = (storeName: string | undefined) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerCouponsUrl: storeName is missing')
  }

  return `${getRetailerUrl(storeName)}/coupons`
}

export const getRetailerKitsUrl = (storeName: string | undefined) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerKitsUrl: storeName is missing')
  }

  return `${getRetailerUrl(storeName)}/kits`
}

export const getRetailerShoppableVideosUrl = (storeName: string | undefined) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerShoppableVideosUrl: storeName is missing')
  }

  return `${getRetailerUrl(storeName)}/shoppable-videos`
}

export const getRetailerLandingPageUrl = (
  storeName: string | undefined,
  params?: string | string[],
) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerLandingPageUrl: storeName is missing')
  }

  const paramsString = Array.isArray(params) ? params.join('/') : params
  const paramsPath = params ? `/${paramsString}` : ''
  return `${getRetailerUrl(storeName)}/landing-page${paramsPath}`
}

export const getRetailerFeaturedPromotionsUrl = (
  storeName: string | undefined,
  promotionCategoryId: string | number | undefined,
) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerFeaturedPromotionsUrl: storeName is missing')
  }
  if (!promotionCategoryId || promotionCategoryId === '0') {
    return buildGHPFallbackUrlWithReason(
      'getRetailerFeaturedPromotionsUrl: promotionCategoryId is missing',
    )
  }

  return `${getRetailerUrl(storeName)}/featured-promotions/${promotionCategoryId}`
}

export const getRetailerCouponUrl = (storeName: string | undefined, id: string) => {
  if (!storeName) {
    return buildGHPFallbackUrlWithReason('getRetailerCouponUrl: storeName is missing')
  }

  if (!id || id === '0') {
    return buildGHPFallbackUrlWithReason('getRetailerCouponUrl: couponId is missing')
  }

  return `${getRetailerUrl(storeName)}/coupon/${id}`
}

export const basePaths = Object.values(routes).reduce<Record<string, true>>((acc, route) => {
  const segments = route.url.split('/').filter(Boolean)
  // skip root path '/'
  if (segments.length === 0) return acc

  const basePath = `/${segments[0]}`

  return { ...acc, [basePath]: true }
}, {})

export const getIsCurrentRoutePCI = () => {
  if (isOnServer()) return false
  return pciRouteMapByURL.has(window.location.pathname)
}
